// ErrorBoundary.js
// Component we will use to wrap the top level of our application.

import React from "react";
import errorImage from "../../imgs/error_pic.webp";
import { logError } from "../../api/ErrorApi";

const EXTENSION_URL = /^(?:chrome|moz|safari-web)-extension:\/\//;
const SCRIPT_URL = /(?:https?|(?:chrome|moz|safari-web)-extension):\/\/\S+/;

// True when the error was thrown inside a browser extension script, not our bundle
const isBrowserExtensionError = (filename, stack) => {
  if (filename && EXTENSION_URL.test(filename)) return true;
  const topFrame = (stack || "").match(SCRIPT_URL);
  return Boolean(topFrame && EXTENSION_URL.test(topFrame[0]));
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("Unhandled React error:", error, info);

    // Send error to backend
    logError({
      type: "react_error",
      message: error.toString(),
      stack: error.stack,
      componentStack: info.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    }).catch(() => {
      // Fail silently if error logging fails
    });
  }

  render() {
    const { hasError } = this.state;
    if (hasError) {
      return (
        <div style={{ padding: 32, textAlign: "center" }}>
          <h1>Something went wrong.</h1>
          <img src={errorImage} alt="Error" />
          <p>
            Please refresh the page or contact support if you encounter more
            issues.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

window.addEventListener("unhandledrejection", (evt) => {
  const reason = evt.reason;
  console.error("Unhandled promise rejection:", reason);

  // Build a useful message even when reason is null/undefined/non-Error
  let message = "Unknown rejection";
  let stack = "";
  let reasonDetail = null;

  if (reason instanceof Error) {
    message = reason.message || reason.toString();
    stack = reason.stack || "";
  } else if (reason !== null && reason !== undefined) {
    // Non-Error rejection (string, object, number, etc.)
    try {
      message =
        typeof reason === "string"
          ? reason
          : JSON.stringify(reason, null, 2).slice(0, 2000);
    } catch {
      message = String(reason);
    }
    reasonDetail = typeof reason;
  } else {
    // reason is null or undefined - capture what we can
    reasonDetail = `reason was ${reason === null ? "null" : "undefined"}`;
  }

  if (isBrowserExtensionError(null, stack)) return;

  logError({
    type: "unhandled_rejection",
    message,
    stack,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
    metadata: {
      reasonType: typeof reason,
      reasonDetail,
      reasonConstructor: reason?.constructor?.name || null,
    },
  }).catch(() => {});
});

window.addEventListener("error", (evt) => {
  const stack = evt.error?.stack || "";
  // Extension errors (e.g. ones wrapping XHR) would re-fire on every log POST
  if (isBrowserExtensionError(evt.filename, stack)) return;

  console.error("Global JS error:", evt.error);

  logError({
    type: "global_error",
    message: evt.message || "Unknown error",
    filename: evt.filename,
    lineno: evt.lineno,
    colno: evt.colno,
    stack,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  }).catch(() => {});
});

export default ErrorBoundary;
